import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "fenco-cookie-consent";

/**
 * Broadcast that lets the Footer "Manage cookies" link reopen the banner.
 * The Footer dispatches `window.dispatchEvent(new Event("fenco:open-cookie-banner"))`.
 */
export const OPEN_EVENT = "fenco:open-cookie-banner";

function applyConsent(choice) {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch (e) { /* storage unavailable */ }

  const ph = typeof window !== "undefined" ? window.posthog : null;
  if (!ph) return;

  if (choice === "accepted") {
    try {
      ph.opt_in_capturing && ph.opt_in_capturing();
      ph.startSessionRecording && ph.startSessionRecording();
    } catch (e) { /* posthog not ready */ }
  } else {
    try {
      ph.opt_out_capturing && ph.opt_out_capturing();
      ph.stopSessionRecording && ph.stopSessionRecording();
    } catch (e) { /* posthog not ready */ }
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show on first visit (no prior choice), or when Footer link asks to reopen.
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
    } catch (e) { /* fall through */ }

    const handler = () => setVisible(true);
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, []);

  if (!visible) return null;

  const accept = () => { applyConsent("accepted"); setVisible(false); };
  const reject = () => { applyConsent("rejected"); setVisible(false); };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      data-testid="cookie-banner"
      className="fixed bottom-4 right-4 left-4 md:right-6 md:left-auto md:bottom-6 md:max-w-md z-[70]"
    >
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 p-5 md:p-6">
        <button
          onClick={reject}
          aria-label="Reject cookies and close"
          data-testid="cookie-banner-close"
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 p-1 rounded-full"
        >
          <X size={16} />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center border border-orange-100 shrink-0">
            <Cookie size={18} className="text-[#FF5A1F]" />
          </div>
          <div className="flex-1">
            <p className="font-display text-base font-bold text-gray-900">We use a few cookies</p>
            <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
              We&rsquo;d like to use analytics cookies to help us improve the site. Only essential cookies run by default. Read the{" "}
              <Link to="/privacy" className="text-[#FF5A1F] font-semibold hover:underline" data-testid="cookie-banner-privacy-link">
                Privacy Policy
              </Link>.
            </p>

            <div className="mt-4 flex gap-2.5">
              <button
                onClick={reject}
                data-testid="cookie-banner-reject"
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-full transition"
              >
                Reject
              </button>
              <button
                onClick={accept}
                data-testid="cookie-banner-accept"
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-[#FF5A1F] hover:bg-[#E04B15] rounded-full transition shadow-lg shadow-orange-500/20"
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
